/**
 * Keeping a timeline's sections on its song's parts (2026-10-21, `songParts.ts`, `store.ts::followSongParts`).
 *
 * A timeline that follows its song (`testTrack.partsFollow`) is relabelled whenever the song's parts change, wherever
 * that happened (this timeline's song row, another timeline using the song, the Audio library, an undo of any of
 * them), so OPBridge always finds each section's mark by its name. The relabel moves nothing and is not an undo step
 * of its own, so one Undo of the song's edit puts both back.
 *
 * A timeline not following yet starts when the operator marks a part on its song row, or says "Use the song's parts"
 * (`startFollowing`); one with nothing on its lanes starts on its own, as there is nothing to move.
 */
import { createEffect, createRoot } from 'solid-js';
import type { Timeline } from '@opbuttons/schema';
import { followSongParts, timelines } from './store.ts';
import { beatmaps, patchBeatmap } from './testTrackStore.ts';
import { songBars } from './testTrack.ts';
import { markRange, marksFromParts, moveLine, partAt, partsFromMarks, sectionPlan, setName, splitAt, type SongPart } from './songParts.ts';
import { pushToast } from '../ui/toast.ts';
import { editLocked } from './lease.ts';

/** The sections a timeline would have from its song's parts, or null (no song loaded, clips, no named part). */
export function planFor(t: Timeline): ReturnType<typeof sectionPlan> | null {
  const tt = t.testTrack;
  if (!tt || tt.clips) return null;
  const m = beatmaps()[tt.id];
  if (!m) return null;
  const bars = songBars(m);
  const plan = sectionPlan(partsFromMarks(m.sections, bars));
  if (plan.sections.length) return plan;
  // A following timeline whose song has no part left: the whole song, one unnamed section.
  return tt.partsFollow && bars > 0 ? { leadIn: 0, sections: [{ name: '', lengthBars: bars }] } : null;
}

const hasSteps = (t: Timeline) => t.lanes.some((l) => l.steps.length > 0);

/** Start a timeline following its song (one undo step; its steps move with their sections), and say so. */
export function startFollowing(timelineId: string): boolean {
  const t = timelines().find((x) => x.id === timelineId);
  const plan = t ? planFor(t) : null;
  if (!t || !plan) return false;
  const r = followSongParts(timelineId, plan);
  if (!r) return false;
  pushToast(r.dropped ? 'warn' : 'ok', r.dropped
    ? `The sections are now the song's parts. ${r.dropped} change${r.dropped === 1 ? '' : 's'} had no part to go to; Undo brings them back.`
    : "The sections are now the song's parts.", r.dropped ? 5000 : 2500);
  return true;
}

/** Bring every following timeline in step (and start any empty one whose song has parts). */
export function reconcileAll(): void {
  for (const t of timelines()) {
    if (editLocked(t.id)) continue;
    const plan = planFor(t);
    if (!plan) continue;
    if (t.testTrack?.partsFollow) followSongParts(t.id, plan);
    else if (!hasSteps(t)) followSongParts(t.id, plan, { record: false });
  }
}

// ---- editing the song from a following timeline: its bar b (from 0) is the song's bar b + 1 ----

/** The song's parts for timeline `t`, or null (no song loaded). */
function partsOf(t: Timeline): SongPart[] | null {
  const m = t.testTrack ? beatmaps()[t.testTrack.id] : undefined;
  return m ? partsFromMarks(m.sections, songBars(m)) : null;
}
function saveParts(t: Timeline, next: SongPart[]): void {
  if (t.testTrack) void patchBeatmap(t.testTrack.id, { sections: marksFromParts(next) });
}

/** The part a timeline bar is in (index into `partsOf`), or -1. */
export function partIndexAtBar(t: Timeline, timelineBar: number): number {
  const ps = partsOf(t);
  return ps ? partAt(ps, Math.floor(timelineBar) + 1) : -1;
}

/** Name the part at a timeline bar (null = not a part). */
export function nameSongPartAt(t: Timeline, timelineBar: number, name: string | null): void {
  const ps = partsOf(t); if (!ps) return;
  const i = partAt(ps, Math.floor(timelineBar) + 1);
  if (i >= 0) saveParts(t, setName(ps, i, name));
}

/** Split the song's part at a timeline bar there. */
export function splitSongAt(t: Timeline, timelineBar: number): void {
  const ps = partsOf(t); if (!ps) return;
  saveParts(t, splitAt(ps, Math.round(timelineBar) + 1));
}

/** Move the line that starts at timeline bar `from` to timeline bar `to` (a section edge dragged). The first line
 *  (the song's start) makes or resizes the unnamed bars before the first part; the song's end does not move. */
export function moveSongLine(t: Timeline, from: number, to: number): void {
  const ps = partsOf(t); if (!ps) return;
  const songFrom = Math.round(from) + 1; const songTo = Math.round(to) + 1;
  const i = ps.findIndex((p) => p.from === songFrom);
  // The lead-in dragged back to nothing: the first part starts the song again.
  if (i === 1 && !ps[0]!.name && songTo <= 1) { saveParts(t, markRange(ps, 1, ps[1]!.to, ps[1]!.name).parts); return; }
  if (i > 0) { saveParts(t, moveLine(ps, i - 1, songTo)); return; }
  if (i === 0 && songTo > 1 && ps[0]!.name) {
    // The first part starts later: the bars before it are not a part (the lead-in).
    saveParts(t, markRange(ps, 1, Math.min(songTo, ps[0]!.to) - 1, null).parts);
  }
}

createRoot(() => {
  createEffect(() => { beatmaps(); timelines(); reconcileAll(); });
});
